/**
 * Required statutory document counts for the groups Compliance Status chart.
 */

import { COMPLIANCE_COLORS } from 'src/features/reporting/utils/color';
import { TRAINING_EXPIRING_WINDOW_LABEL } from 'src/features/training-hub/trainingStatus';

import type { DirectoryStatutoryRow, GroupStatutoryDocumentsResponse, StatutoryDocumentRow } from '../api';
import { hasStatutoryFileOrNoteEvidence } from './StatutoryComplianceSummary';

type StatutoryCountSource = Pick<StatutoryDocumentRow, 'required' | 'expiry_status'> & {
  evidenced?: boolean;
  dismissed?: boolean;
  dismissal?: StatutoryDocumentRow['dismissal'];
  linked_documents?: StatutoryDocumentRow['linked_documents'];
  other_evidence?: StatutoryDocumentRow['other_evidence'];
  training_evidence?: StatutoryDocumentRow['training_evidence'];
};

function isDismissed(document: StatutoryCountSource): boolean {
  return document.dismissed === true || document.dismissal != null;
}

/** Label copy for the backend's Expiring window on ordinary evidence. */
const STATUTORY_EXPIRING_WINDOW_LABEL = 'within 3 months';

/** Label copy for the window a row's ``expiry_status`` uses; training-sourced rows follow the Training Record window. */
export function statutoryExpiringWindowLabel(document: Pick<StatutoryDocumentRow, 'sourced_from_training'>): string {
  return document.sourced_from_training ? TRAINING_EXPIRING_WINDOW_LABEL : STATUTORY_EXPIRING_WINDOW_LABEL;
}

export const STATUTORY_PRESENT_LABEL = 'Evidenced';
export const STATUTORY_PENDING_LABEL = 'Pending';
export const STATUTORY_EXPIRING_LABEL = 'Expiring';
export const STATUTORY_EXPIRED_LABEL = 'Expired';
export const STATUTORY_MISSING_LABEL = 'Missing';

export const STATUTORY_COMPLIANCE_COLORS = {
  present: COMPLIANCE_COLORS[5],
  pending: COMPLIANCE_COLORS[2],
  expiring: COMPLIANCE_COLORS[4],
  expired: COMPLIANCE_COLORS[1],
  missing: COMPLIANCE_COLORS.na
} as const;

export const STATUTORY_COMPLIANCE_SERIES = [
  { label: STATUTORY_PRESENT_LABEL, color: STATUTORY_COMPLIANCE_COLORS.present },
  { label: STATUTORY_EXPIRING_LABEL, color: STATUTORY_COMPLIANCE_COLORS.expiring },
  { label: STATUTORY_PENDING_LABEL, color: STATUTORY_COMPLIANCE_COLORS.pending },
  { label: STATUTORY_EXPIRED_LABEL, color: STATUTORY_COMPLIANCE_COLORS.expired },
  { label: STATUTORY_MISSING_LABEL, color: STATUTORY_COMPLIANCE_COLORS.missing }
] as const;

export type StatutoryDocumentCounts = {
  present: number;
  expiring: number;
  pending: number;
  expired: number;
  missing: number;
};

export function countRequiredStatutoryDocuments(documents: readonly StatutoryCountSource[]): StatutoryDocumentCounts {
  const counts: StatutoryDocumentCounts = { present: 0, expiring: 0, pending: 0, expired: 0, missing: 0 };
  for (const document of documents) {
    if (!document.required || isDismissed(document)) continue;
    const evidenced = document.evidenced ?? hasStatutoryFileOrNoteEvidence(document as StatutoryDocumentRow);
    if (!evidenced) {
      counts.missing += 1;
      continue;
    }
    if (document.expiry_status === 'expired') {
      counts.expired += 1;
    } else if (document.expiry_status === 'pending') {
      counts.pending += 1;
    } else if (document.expiry_status === 'expiring') {
      counts.expiring += 1;
    } else {
      counts.present += 1;
    }
  }
  return counts;
}

export function statutoryCountTotal(counts: StatutoryDocumentCounts): number {
  return counts.present + counts.expiring + counts.pending + counts.expired + counts.missing;
}

export function statutoryComplianceSegments(counts: StatutoryDocumentCounts): {
  label: string;
  value: number;
  color: string;
}[] {
  return [
    { label: STATUTORY_PRESENT_LABEL, value: counts.present, color: STATUTORY_COMPLIANCE_COLORS.present },
    {
      label: STATUTORY_EXPIRING_LABEL,
      value: counts.expiring,
      color: STATUTORY_COMPLIANCE_COLORS.expiring
    },
    { label: STATUTORY_PENDING_LABEL, value: counts.pending, color: STATUTORY_COMPLIANCE_COLORS.pending },
    { label: STATUTORY_EXPIRED_LABEL, value: counts.expired, color: STATUTORY_COMPLIANCE_COLORS.expired },
    { label: STATUTORY_MISSING_LABEL, value: counts.missing, color: STATUTORY_COMPLIANCE_COLORS.missing }
  ];
}

export function hasStatutoryDocumentsConfigured(
  response: GroupStatutoryDocumentsResponse | { documents: readonly DirectoryStatutoryRow[] } | undefined
): boolean {
  return response != null && statutoryCountTotal(countRequiredStatutoryDocuments(response.documents)) > 0;
}
